import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { CostEstimateRequest, CostEstimateResponse } from 'ts-types/api.types';
import { getTreatmentCostEstimate } from 'services/costEstimationServices';
import axios, { CancelTokenSource } from 'axios';
import { Button, Grid, Header, Modal, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { currencyFormatter, numberFormatter } from 'utils/fomattingUtils';
import LoaderComponent from 'components/LoaderComponent';
import { noop } from 'utils/functionUtils';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';

const ScrollableTableWrapper = styled.div`
  height: 445px;
  overflow-y: auto;
  width: 100%;
  box-shadow: none;
  padding-right: 10px;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }
`;

interface Props extends MessageSourceProps {
  modalOpen: boolean,
  onModalClose: () => void,
  costEstimateRequest?: CostEstimateRequest
}

interface State {
  costEstimate?: CostEstimateResponse,
  loadingData: boolean,
  cancelTokenSource: CancelTokenSource
}

class TreatmentCostEstimate extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      loadingData: false,
      cancelTokenSource: cancelTokenSource
    };
  }

  componentDidUpdate({modalOpen: prevModalOpen}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {modalOpen, costEstimateRequest} = this.props;

    if (modalOpen != prevModalOpen && modalOpen && !!costEstimateRequest) {
      this.getTreatmentCostEstimate();
    }
  }

  getTreatmentCostEstimate() {

    const {costEstimateRequest} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      loadingData: true
    });

    getTreatmentCostEstimate(cancelTokenSource, costEstimateRequest!).then(response => {
      this.setState({
        costEstimate: response,
        loadingData: false
      })
    }).catch(noop)
  }

  render(): React.ReactNode {

    const {loadingData} = this.state;
    const {getMessage} = this.props;

    return loadingData
        ? <LoaderComponent
            message={getMessage("costEstimate.loading")}
            dimmerProps={{active: true, inverted: false, page: true}}
        />
        : this.renderModal();
  }

  renderModal(): React.ReactNode {

    const {modalOpen, onModalClose, getMessage} = this.props;
    return (
        <Modal
            open={modalOpen}
            onClose={onModalClose}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {getMessage("costEstimate")}
          </Modal.Header>
          <FormModalContentContainer>
            {this.renderContent()}
          </FormModalContentContainer>
          <Modal.Actions>
            <Button type="button" primary compact onClick={onModalClose}>{getMessage('button.ok')}</Button>
          </Modal.Actions>
        </Modal>
    );
  };

  renderContent = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {costEstimate} = this.state;

    return (
        <Grid>
          <Grid.Row>
            <Grid.Column width={5}>
              <Header size="small">
                {`${getMessage("costEstimate.total")}: ${costEstimate && currencyFormatter.format(costEstimate.totalPrice)}`}
              </Header>
            </Grid.Column>
            <Grid.Column width={5}>
              <Header size="small">
                {`${getMessage("costEstimate.used")}: ${costEstimate && currencyFormatter.format(costEstimate.usedPrice)}`}
              </Header>
            </Grid.Column>
            <Grid.Column width={5}>
              <Header size="small">
                {`${getMessage("costEstimate.open")}: ${costEstimate && currencyFormatter.format(costEstimate.openPrice)}`}
              </Header>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            {this.renderTable()}
          </Grid.Row>
        </Grid>
    )
  };

  renderTable = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {costEstimate} = this.state;

    const mappedCostEstimateEntries = !!costEstimate ? costEstimate.costEstimateEntries.map((costEstimateEntry, index) => {
      return (
          <Table.Row key={index} disabled={costEstimateEntry.openQuantity <= 0}>
            <Table.Cell>{numberFormatter.format(costEstimateEntry.totalQuantity)}</Table.Cell>
            <Table.Cell>{numberFormatter.format(costEstimateEntry.usedQuantity)}</Table.Cell>
            <Table.Cell>{numberFormatter.format(costEstimateEntry.openQuantity)}</Table.Cell>
            <Table.Cell>{getMessage(costEstimateEntry.description)}</Table.Cell>
            <Table.Cell>{numberFormatter.format(costEstimateEntry.value)}</Table.Cell>
            <Table.Cell>{costEstimateEntry.procedureType}</Table.Cell>
          </Table.Row>
      )
    }) : [];

    return (
        <ScrollableTableWrapper>
          <Table celled style={{marginLeft: "10px", marginRight: "10px"}}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>{getMessage("costEstimate.quantity.short")}</Table.HeaderCell>
                <Table.HeaderCell>{getMessage("costEstimate.used")}</Table.HeaderCell>
                <Table.HeaderCell>{getMessage("costEstimate.open")}</Table.HeaderCell>
                <Table.HeaderCell>{getMessage("description")}</Table.HeaderCell>
                <Table.HeaderCell>{getMessage("value")}</Table.HeaderCell>
                <Table.HeaderCell>{getMessage("type")}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {mappedCostEstimateEntries}
            </Table.Body>
          </Table>
        </ScrollableTableWrapper>
    )
  };

}

export default withMessages(TreatmentCostEstimate);
